/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import zipcelx from 'zipcelx';
import { cloneDeep } from 'lodash';
import Highcharts, { Options as HighchartsOptions } from 'highcharts';
import '~/shared/utils/highchartsAllowList';
import HCAccessibility from 'highcharts/modules/accessibility';
import Humanize from 'humanize-plus';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCExportData from 'highcharts/modules/export-data';
import HCNoData from 'highcharts/modules/no-data-to-display';
import styled from 'styled-components';
import reactGA from 'react-ga';
import CustomEvents from 'highcharts-custom-events';
import moment from 'moment';
import { hex2rgba } from '../../../utils/colors/hex2rgba';
import { buildChartExportFileName } from '~/shared/utils/buildChartExportFileName';

HCExporting(Highcharts);
HCExportData(Highcharts);
HCNoData(Highcharts);
HCOfflineExporting(Highcharts);
CustomEvents(Highcharts as any);
HCAccessibility(Highcharts);

const customEvent = (Highcharts.Chart.prototype as any).customEvent;
const orgAdd = customEvent.add;
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};
customEvent.add = (SVGelem: any, events: any, elemObj: any, eventElement: any, isPoint: any) => {
  if (events) {
    for (const key in events) {
      if (events[key] === undefined) {
        events[key] = noop;
      }
    }
  }
  orgAdd.call(this, SVGelem, events, elemObj, eventElement, isPoint);
};

(function (H) {
  H.Chart.prototype.downloadXLS = function () {
    const div = document.createElement('div');
    let xlsxRows = [];
    div.style.display = 'none';
    document.body.appendChild(div);
    const rows = this.getDataRows(true);
    xlsxRows = H.map(rows.slice(1), function (row: any) {
      return H.map(row, function (column: any) {
        let tempColumn = column;
        if (typeof column === 'string' && column?.includes('00:00:00')) {
          tempColumn = moment(column).format('MM/DD/YYYY');
        }
        return {
          type: typeof column === 'number' ? 'number' : 'string',
          value: tempColumn,
        };
      });
    });

    const name = this.options.exporting!.filename;
    zipcelx({
      filename: name as unknown as string,
      sheet: {
        data: xlsxRows,
      },
    });
  };
})(Highcharts);

const EMPTY_OPTIONS: HighchartsOptions = { series: [] };

export interface IStackBarChart {
  axisXLabels: string[];
  axisYLabels: string[];
  axisYIds: string[];
  values: { XLabel: string; YLabel: string; Value: number }[];
  matrix: number[][];
  data?: any;
}

interface ISeriesObj {
  name: string;
  data: any[];
  color: string | undefined;
  allowPointSelect: boolean;
}

const Root = styled.div<{ $gridheight: number }>`
  height: ${({ $gridheight }) =>
    $gridheight ? `${$gridheight * 110 + ($gridheight / 3 - 1) * 30}px` : 'calc(100vh - 145px)'};
  width: 100%;
  position: relative;
  margin-top: 10px;
`;

const HighchartContainer = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  border: solid 1px var(--border-chart);
  background: var(--background-chart);
  padding: 10px;
  .highcharts-legend-item > .highcharts-point {
    transform: unset !important;
  }
  .highcharts-axis {
    display: none;
  }
  .highcharts-button-box {
    x: 2;
    y: 1;
    rx: 16;
    ry: 16;
    &:hover {
      fill: #d4e5ff !important;
    }
  }
  .highcharts-button-symbol {
    width: 3.5px !important;
    fill: #4c5e72 !important;
    stroke: transparent !important;
    pointer-events: none;
  }
`;

Highcharts.SVGRenderer.prototype.symbols.ellipsisvertical = function () {
  const path = [
    'M18.1538 16C18.1538 17.1451 17.222 18.0769 16.0769 18.0769C14.9318 18.0769 14 17.1451 14 16C14 14.8549 14.9318 13.9231 16.0769 13.9231C17.222 13.9231 18.1538 14.8549 18.1538 16Z M18.1538 22.9231C18.1538 24.0682 17.222 25 16.0769 25C14.9318 25 14 24.0682 14 22.9231C14 21.778 14.9318 20.8462 16.0769 20.8462C17.222 20.8462 18.1538 21.778 18.1538 22.9231Z M18.1538 9.07692C18.1538 10.222 17.222 11.1538 16.0769 11.1538C14.9318 11.1538 14 10.222 14 9.07692C14 7.93185 14.9318 7 16.0769 7C17.222 7 18.1538 7.93185 18.1538 9.07692Z',
  ];
  return path;
};

export default function ScatterChart({
  data,
  size,
  onClick,
  prism,
  filterValues,
  colorScheme,
  chartTitle,
  chartSubtitle,
  chartYtitle,
  tooltipCfg,
  gridHeight,
}: any) {
  const chartRef = useRef<any>(null);
  const [selectedLabel, setSelectedLabel] = useState(false);
  const [labelClicked, setLabelClicked] = useState(false);

  const exportFileName = buildChartExportFileName(chartTitle, filterValues, prism);

  const colorsBg = data?.highcharts_config?.custom_background_colors;
  const labelsBg = data?.highcharts_config?.custom_background_labels;
  const quadrantColors = colorsBg;
  const quadrantLabels = labelsBg;

  const drawQuadrants = (chart: any, update: any) => {
    if (!quadrantColors) return;
    const xAxis = chart.xAxis[0],
      yAxis = chart.yAxis[0],
      x0 = Math.max(xAxis.toPixels(0), xAxis.left),
      y0 = yAxis.toPixels(0),
      rightTo0 = Math.max(yAxis.width - x0 + yAxis.left, 0),
      leftTo0 = Math.max(x0 - yAxis.left, 0),
      topTo0 = Math.max(y0 - yAxis.top, 0),
      bottomTo0 = Math.max(yAxis.height - y0 + yAxis.top, 0);

    if (!chart.quadrants) {
      chart.quadrants = {
        I: chart.renderer
          .rect()
          .attr({
            fill: data?.quadrantColors?.[0]
              ? hex2rgba(data?.quadrantColors?.[0], 1.0)
              : hex2rgba(quadrantColors[0], 1.0),
          })
          .add(),
        II: chart.renderer
          .rect()
          .attr({
            fill: data?.quadrantColors?.[1]
              ? hex2rgba(data?.quadrantColors?.[1], 1.0)
              : hex2rgba(quadrantColors[1], 1.0),
          })
          .add(),
        III: chart.renderer
          .rect()
          .attr({
            fill: data?.quadrantColors?.[2]
              ? hex2rgba(data?.quadrantColors?.[2], 1.0)
              : hex2rgba(quadrantColors[2], 1.0),
          })
          .add(),
        IV: chart.renderer
          .rect()
          .attr({
            fill: data?.quadrantColors?.[3]
              ? hex2rgba(data?.quadrantColors?.[3], 1.0)
              : hex2rgba(quadrantColors[3], 1.0),
          })
          .add(),
      };
    }

    chart.quadrants.I[update]({
      x: x0,
      y: xAxis.top,
      width: rightTo0,
      height: topTo0,
    });

    chart.quadrants.II[update]({
      x: xAxis.left,
      y: xAxis.top,
      width: leftTo0,
      height: topTo0,
    });

    chart.quadrants.III[update]({
      x: xAxis.left,
      y: y0,
      width: leftTo0,
      height: bottomTo0,
    });

    chart.quadrants.IV[update]({
      x: x0,
      y: y0,
      width: rightTo0,
      height: bottomTo0,
    });
  };

  const getTextWidth = useCallback((text: string, font: any) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context === null) return 0;

    context.font = font || getComputedStyle(document.body).font;

    return context.measureText(text).width;
  }, []);

  const drawQuadrantsLables = (chart: any, size: any) => {
    if (!quadrantLabels) return;
    const r = chart.renderer,
      each = Highcharts.each,
      labels = data?.quadrantLabels || quadrantLabels,
      labelstyles = {
        'font-size': '22px',
        color: '#000000',
      },
      attr = {
        zindex: 10,
      };

    let bbox = null,
      x: any = null,
      y: any = null;

    chart.label = [];

    //render
    labels.forEach((label: string, i: number) => {
      chart.label[i] = r.text(label, 0, 0).attr(attr).css(labelstyles).add();
      const { width, height } = chart.clipBox;
      bbox = chart.clipBox;
      switch (i) {
        case 0:
          x = 70;
          y = 85;
          break;
        case 1:
          x = bbox.width + 63 - getTextWidth(labels[1], '22px Montserrat');
          y = 85;
          break;
        case 2:
          x = 70;
          y = bbox.height + 130;
          break;
        case 3:
          x = bbox.width + 63 - getTextWidth(labels[3], '22px Montserrat');
          y = bbox.height + 130;
          break;
      }

      chart.label[i].attr({
        x: x,
        y: y,
      });
    });
  };

  useEffect(() => {
    function handleClick(e: any) {
      const file = (e.target as Element).getAttribute('download');
      if (file) {
        const fileType = file.substr(file.length - 3, 3);
        reactGA.event({ category: 'User', action: `Downloaded ${fileType}: ${file}` });
      }
    }
    window.addEventListener('click', handleClick);

    return function cleanupListener() {
      window.removeEventListener('click', handleClick);
    };
  }, [chartRef]);

  useEffect(() => {
    chartRef.current.reflow();
  }, [chartRef, size]);

  const chartOptions = useMemo(() => {
    const sortedData: IStackBarChart = cloneDeep({
      axisYLabels: data?.row_nodes?.map((r: any) => r.name),
      axisYIds: data?.row_nodes?.map((r: any) => r.id),
      axisXLabels: data?.col_nodes?.map((c: any) => c.name),
      matrix: data?.data,
      data: data,
      values: [],
    });

    const categoriesData: string[] = [];
    const seriesData: any[] = [];

    for (let j = 0; j < 1; ++j) {
      const seriesObj: ISeriesObj = {
        name: sortedData?.axisXLabels?.[0],
        data: [],
        color: colorScheme ? colorScheme[0] : undefined,
        allowPointSelect: true,
      };
      for (let i = 0; i < sortedData.axisYLabels?.length; ++i) {
        if (j === 0) categoriesData.push(sortedData.axisYLabels[i]);
        const objData = {
          name: sortedData.axisYLabels?.[i],
          id: sortedData.axisYIds?.[i],
          x: data?.data?.[i]?.[0],
          y: data?.data?.[i]?.[1],
          showX: Humanize.toFixed(data?.data?.[i]?.[0], 4),
          showY: Humanize.toFixed(data?.data?.[i]?.[1], 4),
        };
        seriesObj.data.push(objData);
      }
      seriesData.push(seriesObj);
    }

    if (!data) return;
    return {
      chart: {
        events: {
          load: () => {
            drawQuadrants(chartRef.current, 'attr');
            if (!chartRef.current) return;
            drawQuadrantsLables(chartRef.current, size);
          },
          redraw: () => {
            drawQuadrants(chartRef.current, 'animate');
          },
        },
        id: 'scatter',
        width: size.width - 46,
        height: size.height - gridHeight * 5,
        marginTop: 100,
        marginLeft: 70,
        type: 'scatter',
        zoomType: 'xy',
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
          color: 'var(--color-chart-scatter-labels)',
        },
      },
      rangeSelector: {
        inputEnabled: false,
        selected: 5,
      },
      exporting: {
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof Highcharts.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        filename: exportFileName,
        buttons: {
          contextButton: {
            x: 14,
            y: 6,
            symbol: 'ellipsisvertical',
            menuClassName: 'highcharts-indicator-menu',
            menuItems: [
              'viewFullscreen',
              'downloadPNG',
              'downloadSVG',
              'downloadPDF',
              'separator',
              'downloadXLS',
              'downloadCSV',
            ],
          },
        },
        fallbackToExportServer: false,
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 22,
        y: 24,
        style: {
          fontSize: '18px',
          color: 'var(--color-chart-title)',
          fontWeight: 500,
          letterSpacing: '0.5px',
        },
      },
      subtitle: {
        text: chartSubtitle,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      yAxis: {
        endOnTick: false,
        minorTicks: false,
        opposite: false,
        gridLineWidth: 0,
        gridZIndex: -1,
        min: -1,
        max: 1,
        title: {
          text: 'Normalized Intensity',
          x: 50,
        },
        labels: {
          useHTML: false,
          style: {
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontSize: '14px',
          },
          x: -34,
          align: 'left',
          minWidth: 160,
          width: 160,
        },
        plotLines: [
          {
            value: 0,
            width: 2,
            color: 'silver',
          },
          {
            value: -1,
            width: 1,
            color: 'silver',
          },
        ],
      },
      xAxis: {
        minorTicks: false,
        lineColor: 'transparent',
        tickLength: 0,
        gridLineWidth: 0,
        title: {
          text: 'Normalized R-Squared',
        },
        min: -1,
        max: 1,
        plotLines: [
          {
            value: 0,
            width: 2,
            color: 'silver',
          },
          {
            value: -1,
            width: 1,
            color: 'silver',
          },
        ],
        labels: {
          distance: 40,
        },
      },
      plotOptions: {
        scatter: {
          marker: {
            radius: 5,
            fillColor: 'var(--color-chart-scatter-marker)',
            states: {
              hover: {
                enabled: true,
                lineColor: 'var(--color-chart-scatter-hover)',
              },
              select: {
                radius: 8,
                fillColor: 'var(--color-chart-scatter-marker-selected)',
                lineColor: 'var(--color-chart-scatter-hover)',
                lineWidth: 1,
              },
            },
          },
          states: {
            hover: {
              marker: {
                enabled: false,
              },
            },
          },
          tooltip: {
            useHTML: true,
            headerFormat: ``,
            pointFormat: `<span style="font-size:16px;line-height:25px;color:var(--color-chart-scatter-labels)">{point.name}</span></br><span style="font-size:5px;color:#fff">a</span></br><span style="font-size:12px;line-height:25px;color:var(--color-chart-scatter-labels)">Norm. R-Squared: {point.showX},</span></br><span style="font-size:12px;line-height:25px;color:var(--color-chart-scatter-labels)">Norm. Intensity: {point.showY}</span>`,
          },
        },
        series: {
          type: 'scatter',
          dataLabels: {
            enabled: true,
            format: '{point.name}',
            padding: 8,
            style: {
              fill: 'var(--color-chart-scatter-labels)',
              textOutline: 'none',
              fontWeight: 'normal',
            },
            // change 'allowOverlap' to true to see all labels - overlapping!
            allowOverlap: false,
          },
          selected: true,
          showInNavigator: true,
          animation: false,
          line: {
            selected: false,
          },
          point: {
            events: {
              click: (e: any) => {
                e.preventDefault;
                const cellInfo = {
                  source: 'value',
                  topic: e.point.options.id,
                };
                setSelectedLabel(e.point.options.id);
                setLabelClicked(true);
                onClick(cellInfo);
              },
            },
          },
        },
      },
      lang: {
        noData: '',
      },
      noData: {
        style: {
          fontWeight: 'bold',
          fontSize: '15px',
          color: 'var(--color-chart-no-data)',
        },
      },
      series: seriesData,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    size,
    exportFileName,
    onClick,
    chartRef,
    labelClicked,
    selectedLabel,
    colorScheme,
    tooltipCfg,
    chartTitle,
    chartSubtitle,
    chartYtitle,
  ]);

  return (
    <Root $gridheight={gridHeight}>
      <HighchartContainer>
        <HighchartsReact
          containerProps={{ style: { height: '100%' } }}
          style={{ flex: 1 }}
          highcharts={Highcharts}
          constructorType={'chart'}
          options={chartOptions ?? EMPTY_OPTIONS}
          callback={(chart: any) => (chartRef.current = chart)}
        />
      </HighchartContainer>
    </Root>
  );
}
